import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSubsetExportData } from '../../../api/hooks/useSubsetExportData/useSubsetExportData';
import { ControlItemValue, Dataset, GridRow } from '../../../types/types';
import { DEFAULT_EXPORT_COLUMN_OPTIONS } from '../constants';
import { buildExportSections } from '../utils/buildExportSections';
import { canExportSubsetsOnly } from '../utils/canExportSubsetsOnly';
import hasInvalidExportColumnOptions from '../utils/hasInvalidExportColumnOptions';
import { mergeExportColumns } from '../utils/mergeExportColumns';
import { mergeExportSections } from '../utils/mergeExportSections';
import { reorderSectionColumns } from '../utils/reorderSectionColumns';
import { resolveExportColumns } from '../utils/resolveExportColumns';
import {
  ExportColumnOptions,
  ExportColumnRoundingMode,
  ExportSection,
  SubsetExportState,
} from '../types';

type UseExportSectionsParams = {
  exportDataset: Dataset | undefined;
  rows: GridRow[];
  isWithSubsetsMode: boolean;
  subsetExportState: SubsetExportState | undefined;
  slaveDatasets: Record<string, Dataset>;
};

export type UseExportSectionsResult = {
  sections: ExportSection[];
  subsetDatasets: Record<string, Dataset>;
  loadingDefinitionIds: string[];
  masterSection: ExportSection | undefined;
  subsetSections: ExportSection[];
  activeSection: ExportSection | undefined;
  hasExportableColumns: boolean;
  hasInvalidColumnOptions: boolean;
  isLoadingDefinition: boolean;
  handleToggleSection: (sectionId: string, enabled: boolean) => void;
  handleSelectSection: (section: ExportSection) => void;
  handleToggleColumn: (columnName: string, selected: boolean) => void;
  handleColumnTextOptionChange: (
    columnName: string,
    optionName: keyof Pick<
      ExportColumnOptions,
      'prefix' | 'suffix' | 'maxLength' | 'decimalPlaces'
    >
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  handleRoundingModeChange: (
    columnName: string
  ) => (value: ControlItemValue) => void;
  handleReorderColumns: (
    sectionId: string,
    sourceColumnName: string,
    targetColumnName: string
  ) => void;
};

export const useExportSections = ({
  exportDataset,
  rows,
  isWithSubsetsMode,
  subsetExportState,
  slaveDatasets,
}: UseExportSectionsParams): UseExportSectionsResult => {
  const [sections, setSections] = useState<ExportSection[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string>('');
  const [loadingDefinitionIds, setLoadingDefinitionIds] = useState<string[]>(
    []
  );
  const [subsetDefinitions, setSubsetDefinitions] = useState<
    Record<string, Dataset>
  >({});

  const { loadDatasetDefinition } = useSubsetExportData();

  // Export-loaded definitions stay page-local; the grid owns slaveDatasets and its lazy state.
  const subsetDatasets = useMemo<Record<string, Dataset>>(
    () => ({ ...slaveDatasets, ...subsetDefinitions }),
    [slaveDatasets, subsetDefinitions]
  );

  useEffect(() => {
    const nextSections = buildExportSections(
      exportDataset,
      rows,
      subsetExportState ? {} : subsetDatasets
    );
    setSections((currentSections) =>
      mergeExportSections(currentSections, nextSections)
    );

    // Keep the UI usable after dataset reloads or when a subset definition appears.
    setActiveSectionId((currentSectionId) =>
      nextSections.some((section) => section.id === currentSectionId)
        ? currentSectionId
        : (nextSections[0]?.id ?? '')
    );
  }, [exportDataset, rows, subsetDatasets, subsetExportState]);

  const masterSection = useMemo(
    () => sections.find((section) => section.kind === 'master'),
    [sections]
  );

  const visibleSections = useMemo<ExportSection[]>(() => {
    if (isWithSubsetsMode) return sections;
    if (masterSection) return [masterSection];
    return [];
  }, [isWithSubsetsMode, masterSection, sections]);

  const subsetSections = useMemo<ExportSection[]>(
    () => sections.filter((section) => section.kind === 'subset'),
    [sections]
  );

  const activeSection = useMemo(
    () =>
      isWithSubsetsMode
        ? (sections.find((section) => section.id === activeSectionId) ??
          sections[0])
        : masterSection,
    [activeSectionId, isWithSubsetsMode, masterSection, sections]
  );

  const hasExportableColumns = useMemo<boolean>(
    () =>
      (!!masterSection?.enabled &&
        masterSection.columns.some((column) => column.selected)) ||
      canExportSubsetsOnly(isWithSubsetsMode, exportDataset, sections),
    [exportDataset, isWithSubsetsMode, masterSection, sections]
  );
  const hasInvalidColumnOptions = useMemo(
    () => hasInvalidExportColumnOptions(visibleSections),
    [visibleSections]
  );
  const isLoadingDefinition = loadingDefinitionIds.length > 0;

  const updateSection = useCallback(
    (
      sectionId: string,
      updater: (section: ExportSection) => ExportSection
    ): void => {
      setSections((currentSections) =>
        currentSections.map((section) =>
          section.id === sectionId ? updater(section) : section
        )
      );
    },
    []
  );

  const loadSubsetDefinition = useCallback(
    async (section: ExportSection): Promise<void> => {
      if (
        section.kind !== 'subset' ||
        loadingDefinitionIds.includes(section.id)
      ) {
        return;
      }

      setLoadingDefinitionIds((currentIds) => [...currentIds, section.id]);

      try {
        const subsetDataset = await loadDatasetDefinition({
          subsetDatasetId: section.id,
          cachedSubsetDataset: subsetDatasets[section.id],
        });

        if (!subsetDataset) {
          return;
        }

        setSubsetDefinitions((currentDatasets) => ({
          ...currentDatasets,
          [section.id]: subsetDataset,
        }));

        updateSection(section.id, (currentSection) => ({
          ...currentSection,
          columns: mergeExportColumns(
            resolveExportColumns(
              subsetDataset.items,
              true,
              subsetDataset.idItem
            ),
            currentSection.columns
          ),
          dataAvailable: true,
        }));
      } finally {
        setLoadingDefinitionIds((currentIds) =>
          currentIds.filter((id) => id !== section.id)
        );
      }
    },
    [loadingDefinitionIds, loadDatasetDefinition, subsetDatasets, updateSection]
  );

  const handleToggleSection = useCallback(
    (sectionId: string, enabled: boolean): void => {
      const section = sections.find(
        (currentSection) => currentSection.id === sectionId
      );

      if (!section || section.kind === 'master') {
        // The master dataset is mandatory in both export modes.
        return;
      }

      if (!enabled) {
        // Unchecked subsets drop the export-loaded definition; a grid-cached one is left alone.
        setSubsetDefinitions((currentDatasets) => {
          const nextDatasets = { ...currentDatasets };
          delete nextDatasets[sectionId];
          return nextDatasets;
        });
        updateSection(sectionId, (currentSection) => ({
          ...currentSection,
          enabled: false,
          columns: [],
          rows: [],
          dataAvailable: false,
        }));

        if (activeSectionId === sectionId) {
          setActiveSectionId(masterSection?.id ?? '');
        }
        return;
      }

      updateSection(sectionId, (currentSection) => ({
        ...currentSection,
        enabled,
      }));

      if (enabled && section?.kind === 'subset') {
        // Checking a subset only loads its definition; export rows are resolved on save.
        void loadSubsetDefinition(section);
      }
    },
    [
      masterSection,
      sections,
      updateSection,
      loadSubsetDefinition,
      activeSectionId,
    ]
  );

  const handleSelectSection = useCallback(
    (section: ExportSection): void => {
      if (!isWithSubsetsMode && section.kind !== 'master') {
        return;
      }

      setActiveSectionId(section.id);

      if (section.kind === 'subset' && !section.enabled) {
        handleToggleSection(section.id, true);
      }
    },
    [isWithSubsetsMode, handleToggleSection]
  );

  const handleToggleColumn = useCallback(
    (columnName: string, selected: boolean): void => {
      if (!activeSection) {
        return;
      }

      updateSection(activeSection.id, (section) => ({
        ...section,
        columns: section.columns.map((column) =>
          column.name === columnName ? { ...column, selected } : column
        ),
      }));
    },
    [activeSection, updateSection]
  );

  const handleUpdateColumnOptions = useCallback(
    (columnName: string, options: Partial<ExportColumnOptions>): void => {
      if (!activeSection) {
        return;
      }

      updateSection(activeSection.id, (section) => ({
        ...section,
        columns: section.columns.map((column) =>
          column.name === columnName
            ? {
                ...column,
                // Preserve future option keys even when state was created before this feature.
                exportOptions: {
                  ...DEFAULT_EXPORT_COLUMN_OPTIONS,
                  ...column.exportOptions,
                  ...options,
                },
              }
            : column
        ),
      }));
    },
    [activeSection, updateSection]
  );

  const handleColumnTextOptionChange = useCallback(
    (
      columnName: string,
      optionName: keyof Pick<
        ExportColumnOptions,
        'prefix' | 'suffix' | 'maxLength' | 'decimalPlaces'
      >
    ) =>
      (event: ChangeEvent<HTMLInputElement>): void => {
        handleUpdateColumnOptions(columnName, {
          [optionName]: event.target.value,
        });
      },
    [handleUpdateColumnOptions]
  );

  const handleRoundingModeChange = useCallback(
    (columnName: string) =>
      (value: ControlItemValue): void => {
        handleUpdateColumnOptions(columnName, {
          roundingMode: value as ExportColumnRoundingMode,
        });
      },
    [handleUpdateColumnOptions]
  );

  const handleReorderColumns = useCallback(
    (
      sectionId: string,
      sourceColumnName: string,
      targetColumnName: string
    ): void => {
      updateSection(sectionId, (section) =>
        reorderSectionColumns(section, sourceColumnName, targetColumnName)
      );
    },
    [updateSection]
  );

  return {
    sections,
    subsetDatasets,
    loadingDefinitionIds,
    masterSection,
    subsetSections,
    activeSection,
    hasExportableColumns,
    hasInvalidColumnOptions,
    isLoadingDefinition,
    handleToggleSection,
    handleSelectSection,
    handleToggleColumn,
    handleColumnTextOptionChange,
    handleRoundingModeChange,
    handleReorderColumns,
  };
};
